import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// WeChat scheme: scanning the QR with the WeChat app opens the ID lookup.
// We encode weixin:// so the WeChat app recognises it on scan.
const buildQrUrl = (id) => {
  const data = encodeURIComponent(`weixin://dl/chat?${id}`);
  return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&margin=10&data=${data}`;
};

export const WeChatButton = ({ id, dark = false, testid }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      toast.success("WeChat ID copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`mt-2 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors ${
          dark
            ? "bg-white/10 hover:bg-white/15 text-white border border-white/15"
            : "bg-[#07C160]/10 hover:bg-[#07C160]/15 text-[#07C160] border border-[#07C160]/30"
        }`}
        data-testid={testid || `wechat-btn-${id}`}
        aria-label={`Open WeChat QR for ${id}`}
      >
        <MessageSquare className="w-3.5 h-3.5" /> WeChat · {id}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid={`wechat-modal-${id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-lg">WeChat — {id}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <img
                src={buildQrUrl(id)}
                alt={`WeChat QR for ${id}`}
                width="240"
                height="240"
                data-testid={`wechat-qr-${id}`}
              />
            </div>
            <p className="text-xs text-slate-500 text-center max-w-[260px]">
              Scan with WeChat → <b>Discover → Scan</b>, or copy the ID and search inside WeChat.
            </p>
            <div className="flex items-center gap-2 w-full">
              <div className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 font-mono text-sm text-[#0B132B] select-all" data-testid={`wechat-id-${id}`}>
                {id}
              </div>
              <button
                onClick={copyId}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] text-white px-3.5 py-2 text-xs font-semibold"
                data-testid={`wechat-copy-${id}`}
              >
                {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
